import { Link } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import { trpc } from '../trpc/client';
import HeroWave from '../components/HeroWave';
import {
  Shield, TrendingUp, Star, Globe, Zap, Heart, ChevronRight,
  Users, Lock, Award, Truck, Clock, CheckCircle, ArrowRight,
  Building2, Sun, Car, Hammer, Cpu, Coffee, Factory, Users2,
  GraduationCap, ShieldCheck, TruckIcon, Palette, HeartPulse,
  Monitor, Tractor
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

const iconMap: Record<string, React.ReactNode> = {
  'Tractor': <Tractor className="w-6 h-6" />,
  'Monitor': <Monitor className="w-6 h-6" />,
  'Heart': <HeartPulse className="w-6 h-6" />,
  'Sun': <Sun className="w-6 h-6" />,
  'Building': <Building2 className="w-6 h-6" />,
  'Car': <Car className="w-6 h-6" />,
  'Hammer': <Hammer className="w-6 h-6" />,
  'Cpu': <Cpu className="w-6 h-6" />,
  'Coffee': <Coffee className="w-6 h-6" />,
  'Factory': <Factory className="w-6 h-6" />,
  'Users': <Users2 className="w-6 h-6" />,
  'GraduationCap': <GraduationCap className="w-6 h-6" />,
  'Shield': <ShieldCheck className="w-6 h-6" />,
  'Truck': <TruckIcon className="w-6 h-6" />,
  'Palette': <Palette className="w-6 h-6" />,
};

export default function Home() {
  const { t } = useTranslation();
  const { user } = useStore();
  const { data: categoriesData } = trpc.util.getCategories.useQuery({});
  const { data: featuredDeals } = trpc.deal.list.useQuery({ featured: true, limit: 6 });
  const { data: allDeals } = trpc.deal.list.useQuery({ limit: 8 });

  const categories = categoriesData || [];
  const deals = featuredDeals?.deals || [];
  const recentDeals = allDeals?.deals || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden">
        <HeroWave />
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <div className="liquid-glass-strong rounded-[32px] p-8 md:p-12">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#c2410c]/10 text-[#c2410c] text-xs font-semibold mb-6">
                <Zap className="w-3.5 h-3.5" />
                AI-Powered Global Marketplace
              </div>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-normal tracking-tight text-white mb-6 font-display leading-[1.1]">
                {t('hero.title')}
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl mx-auto leading-relaxed">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/deals"
                  className="px-8 py-4 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-all text-sm font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  {t('hero.browse')}
                </Link>
                <Link
                  to="/supplier/register"
                  className="px-8 py-4 border border-white/30 text-white rounded-xl hover:bg-white/10 transition-all text-sm font-semibold"
                >
                  {t('hero.supplier')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Escrow Trust Strip */}
      <section className="bg-[#0f172a] py-4 relative z-20">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 flex items-center justify-center gap-3 text-white">
          <Shield className="w-5 h-5 text-emerald-400 shrink-0" />
          <p className="text-sm font-medium">{t('escrow.trust')}</p>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1e293b] mb-3 font-display">Browse by Category</h2>
            <p className="text-[#64748b] max-w-xl mx-auto">Explore deals across industries — from agriculture to smart cities, find exactly what your business needs.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/deals?category=${cat.id}`}
                className="group bg-white rounded-2xl p-6 text-center border border-gray-100 hover:border-[#c2410c]/20 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-[#312e81]/10 to-[#c2410c]/10 flex items-center justify-center text-[#4338ca] group-hover:from-[#312e81]/20 group-hover:to-[#c2410c]/20 transition-colors">
                  {iconMap[cat.icon || ''] || <Building2 className="w-6 h-6" />}
                </div>
                <h3 className="text-sm font-semibold text-[#1e293b] mb-1">{cat.name}</h3>
                <p className="text-xs text-[#64748b] line-clamp-2">{cat.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deals Carousel */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1e293b] mb-2 font-display">Trending Deals</h2>
              <p className="text-[#64748b]">Hand-picked by our AI for maximum value</p>
            </div>
            <Link to="/deals" className="hidden md:flex items-center gap-2 text-sm font-medium text-[#c2410c] hover:text-[#9a3412] transition-colors">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#0f172a] text-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-3 font-display">How IPO Works</h2>
            <p className="text-white/60 max-w-xl mx-auto">A secure, transparent marketplace built on trust and technology</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: <Search className="w-8 h-8" />, title: 'Browse Deals', desc: 'Search and filter deals from verified suppliers worldwide. AI matching finds the best deals for you.' },
              { icon: <Lock className="w-8 h-8" />, title: 'Secure Payment', desc: 'Pay securely via our trusted payment partners. Funds are held in escrow until you confirm receipt.' },
              { icon: <Truck className="w-8 h-8" />, title: 'Track Delivery', desc: 'Real-time order tracking from supplier to your door. Full visibility at every step.' },
              { icon: <CheckCircle className="w-8 h-8" />, title: 'Confirm & Release', desc: 'Inspect your order and confirm receipt. Only then are funds released to the supplier.' },
            ].map((step, i) => (
              <div key={i} className="text-center relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-hero flex items-center justify-center">
                  {step.icon}
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#c2410c] flex items-center justify-center text-sm font-bold hidden md:block">
                  {i + 1}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Deals */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1e293b] mb-2 font-display">Recently Added</h2>
              <p className="text-[#64748b]">Fresh deals from our verified supplier network</p>
            </div>
            <Link to="/deals" className="hidden md:flex items-center gap-2 text-sm font-medium text-[#c2410c] hover:text-[#9a3412] transition-colors">
              Browse All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {recentDeals.slice(0, 4).map((deal) => (
              <DealCardCompact key={deal.id} deal={deal} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals / Stats */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '2,500+', label: 'Verified Suppliers', icon: <Users className="w-6 h-6" /> },
              { value: '45,000+', label: 'Deals Listed', icon: <TrendingUp className="w-6 h-6" /> },
              { value: '$1.2B+', label: 'Transaction Volume', icon: <Globe className="w-6 h-6" /> },
              { value: '98.7%', label: 'Satisfaction Rate', icon: <Star className="w-6 h-6" /> },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-[#c2410c]/10 flex items-center justify-center text-[#c2410c] mb-3">
                  {stat.icon}
                </div>
                <p className="text-2xl md:text-3xl font-bold text-[#1e293b] font-display">{stat.value}</p>
                <p className="text-sm text-[#64748b]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#312e81] via-[#4338ca] to-[#c2410c]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4 font-display">Ready to Start Trading?</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses buying and selling on the world's most secure marketplace. Whether you're a buyer, supplier, or affiliate — IPO has a place for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="px-8 py-4 bg-white text-[#4338ca] rounded-xl hover:bg-white/90 transition-all text-sm font-semibold shadow-lg"
            >
              Create Free Account
            </Link>
            <Link
              to="/supplier/register"
              className="px-8 py-4 border border-white/30 text-white rounded-xl hover:bg-white/10 transition-all text-sm font-semibold"
            >
              Register as Supplier
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function DealCard({ deal }: { deal: any }) {
  const { currency } = useStore();
  const mainImage = deal.images?.[0] || '';

  return (
    <Link to={`/deals/${deal.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1.5">
      <div className="relative h-52 overflow-hidden">
        <img
          src={mainImage}
          alt={deal.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3 flex gap-2">
          {deal.featured && (
            <span className="px-2.5 py-1 bg-[#c2410c] text-white text-[10px] font-bold uppercase tracking-wider rounded-lg flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Featured
            </span>
          )}
          <span className="px-2.5 py-1 bg-[#0f172a]/80 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg backdrop-blur-sm flex items-center gap-1">
            <Zap className="w-3 h-3" /> AI Match
          </span>
        </div>
        {deal.dealType === 'service' && (
          <div className="absolute top-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-[#1e293b] text-[10px] font-bold uppercase tracking-wider rounded-lg">
            Service
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-[#c2410c]">{deal.category?.name}</span>
          <span className="text-[#cbd5e1]">|</span>
          <span className="text-xs text-[#64748b] flex items-center gap-1">
            <Award className="w-3 h-3" /> {deal.supplier?.verificationBadge === 'gold_verified' ? 'Gold' : deal.supplier?.verificationBadge === 'premium' ? 'Premium' : 'Verified'}
          </span>
        </div>
        <h3 className="text-base font-semibold text-[#1e293b] mb-2 line-clamp-2 group-hover:text-[#c2410c] transition-colors">{deal.name}</h3>
        <p className="text-xs text-[#64748b] mb-4 line-clamp-2">{deal.shortDescription || deal.description?.slice(0, 100)}...</p>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs text-[#64748b] mb-0.5">Supplier Price + 20%</p>
            <p className="text-2xl font-bold text-[#1e293b] font-display">{currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '$'}{Number(deal.buyerPrice).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-1 text-amber-500">
            <Star className="w-4 h-4 fill-current" />
            <span className="text-sm font-semibold">{deal.avgRating}</span>
            <span className="text-xs text-[#64748b]">({deal.totalOrders})</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function DealCardCompact({ deal }: { deal: any }) {
  const { currency } = useStore();
  const mainImage = deal.images?.[0] || '';

  return (
    <Link to={`/deals/${deal.slug}`} className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1">
      <div className="relative h-40 overflow-hidden">
        <img
          src={mainImage}
          alt={deal.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <p className="text-[10px] font-medium text-[#c2410c] uppercase tracking-wider mb-1">{deal.category?.name}</p>
        <h3 className="text-sm font-semibold text-[#1e293b] mb-2 line-clamp-2 group-hover:text-[#c2410c] transition-colors">{deal.name}</h3>
        <div className="flex items-center justify-between">
          <p className="text-lg font-bold text-[#1e293b] font-display">{currency === 'USD' ? '$' : '€'}{Number(deal.buyerPrice).toLocaleString()}</p>
          <div className="flex items-center gap-1 text-amber-500">
            <Star className="w-3.5 h-3.5 fill-current" />
            <span className="text-xs font-semibold">{deal.avgRating}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function Search(props: { className?: string }) {
  return <SearchIcon {...props} />;
}
import { Search as SearchIcon } from 'lucide-react';
